// Quote-specific formatting and status logic. No DOM, no state, no API.
// Mirrors lib/invoice-format.js: stored statuses are draft / sent / accepted /
// declined / invoiced; "expired" is derived from valid_until the same way
// "overdue" is derived from an invoice's due date.

export function getQuoteIdFromPath(path) {
  const match = String(path || "").match(/^\/quotes\/([^/]+)$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

// Label for a "Valid for" option (quotes are always valid for at least a day,
// so there's no 0-day "on receipt" equivalent).
export function formatValidForOption(days) {
  return `${days} days`;
}

export function formatQuoteStatus(rawStatus) {
  const normalized = String(rawStatus || "").trim().toLowerCase();
  if (!normalized) return "Draft";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function getQuoteStatusTagClass(rawStatus) {
  const normalized = String(rawStatus || "").trim().toLowerCase();
  if (normalized === "accepted") return "app-quote-status-accepted";
  if (normalized === "declined") return "app-quote-status-declined";
  if (normalized === "expired") return "app-quote-status-expired";
  if (normalized === "invoiced") return "app-quote-status-invoiced";
  if (normalized === "sent") return "app-invoice-status-sent";
  return "app-invoice-status-draft";
}

// Takes the quote object. Expiry comes from the server-derived, company-
// timezone `isExpired` flag (see IS_EXPIRED_SQL in server/routes/quotes.js) —
// never from the browser clock. Unlike invoices' derived "overdue" (display
// only), expiry gates whether the client's accept link still works, so the
// app and the public page must agree on which calendar day it flips.
export function getEffectiveQuoteStatus(quote) {
  const normalized = String(quote?.status || "").trim().toLowerCase();
  if (normalized === "sent" && quote?.isExpired) return "expired";
  return normalized || "draft";
}

export function isReadOnlyQuoteStatus(rawStatus) {
  const normalized = String(rawStatus || "").trim().toLowerCase();
  return ["sent", "accepted", "declined", "invoiced"].includes(normalized);
}

function formatQuoteLongDate(rawDate) {
  const parsed = rawDate instanceof Date ? rawDate : new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}

export function formatValidUntilWithDaysLeft(rawValidUntil, isExpired = null) {
  const parsed = new Date(rawValidUntil);
  if (Number.isNaN(parsed.getTime())) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const validUntil = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  const diffDays = Math.round((validUntil.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  const formatted = formatQuoteLongDate(parsed);
  // The day count is computed on the viewer's clock, but the authoritative
  // expiry flag is the server's (company timezone). Inside the boundary
  // window the two can disagree — clamp the wording so the text never
  // contradicts the status tag beside it.
  if (isExpired === true && diffDays >= 0) return `${formatted} (expired)`;
  if (isExpired === false && diffDays < 0) return `${formatted} (expires today)`;
  if (diffDays > 1) return `${formatted} (${diffDays} days left)`;
  if (diffDays === 1) return `${formatted} (1 day left)`;
  if (diffDays === 0) return `${formatted} (expires today)`;
  if (diffDays === -1) return `${formatted} (expired 1 day ago)`;
  return `${formatted} (expired ${Math.abs(diffDays)} days ago)`;
}
